<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('organization_settings.impact_metrics') }}
      </p>
      <p
        class="mt-2 caption blue-grey--text text--darken-1"
        v-html="
          $sanitize($t('organization_settings.impact_metrics_subtitle'), {
            allowedTags: ['a'],
          })
        "
      />
    </div>
    <p class="overline pa-0 my-4 blue-grey--text text--base">
      {{ $t('organization_settings.impact_metrics_reduction') }}
      <v-col cols="7" class="mt-4 pa-0"> <v-divider></v-divider> </v-col>
    </p>

    <div
      v-for="metric in reductionMetrics"
      :key="metric.id"
      class="d-flex align-center py-3"
    >
      <div>
        <p class="body-1 blue-grey--text text--darken-3 mb-0">
          {{ $t(`impacts.${metric.metric.name}`) }}
        </p>
        <p class="body-2 blue-grey--text mb-0">
          {{ getMetricUnits(metric.metric) }}
        </p>
      </div>
      <div class="ml-auto">
        <v-switch
          class="my-0 pa-0"
          hide-details
          :disabled="metric.metric.required"
          :prepend-icon="metric.metric.required ? 'mdi-lock-outline' : ''"
          :data-testid="`metric-switch-${metric.metric.name}`"
          :input-value="metric.required ? true : metric.isEnabled"
          @change="handleSwitchChange($event, metric.metric.id, metric.id)"
        >
        </v-switch>
      </div>
    </div>

    <p class="overline pa-0 my-4 blue-grey--text text--base">
      {{ $t('organization_settings.impact_metrics_increment') }}
      <v-col cols="7" class="mt-4 pa-0"> <v-divider></v-divider> </v-col>
    </p>

    <div
      v-for="metric in incrementMetrics"
      :key="metric.id"
      class="d-flex align-center py-3"
    >
      <div>
        <p class="body-1 blue-grey--text text--darken-3 mb-0">
          {{ $t(`impacts.${metric.metric.name}`) }}
        </p>
        <p class="body-2 blue-grey--text mb-0">
          {{ getMetricUnits(metric.metric) }}
        </p>
      </div>
      <div class="ml-auto">
        <v-switch
          class="my-0 pa-0"
          hide-details
          :data-testid="`metric-switch-${metric.metric.name}`"
          :disabled="metric.metric.required"
          :prepend-icon="metric.metric.required ? 'mdi-lock-outline' : ''"
          :input-value="metric.isEnabled"
          @change="handleSwitchChange($event, metric.metric.id, metric.id)"
        ></v-switch>
      </div>
    </div>
  </v-sheet>
</template>
<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import constants from '@/constants'
import mixin from '@/mixins/mixin.vue'

export default {
  name: 'RevMetrics',

  mixins: [mixin],

  mounted() {
    this.setMetrics()
  },

  data() {
    return {
      reductionMetrics: [],
      incrementMetrics: [],
      constants,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),
  },
  methods: {
    getMetricUnits(metric) {
      const units = []
      metric.units.map(unit =>
        unit.magnitude
          ? units.push(this.$t(`magnitudes.${unit.magnitude}`))
          : null
      )
      return _.compact(_.uniq(units)).join(', ')
    },

    async handleSwitchChange(value, id, configId) {
      try {
        if (configId) {
          await api.metrics.updateRule(this.organizationId, {
            ruleId: configId,
            isEnabled: value,
          })
        } else {
          await api.metrics.createRule(this.organizationId, {
            metricId: id,
            isEnabled: value,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.setMetrics()
      }
    },

    async setMetrics() {
      try {
        const { data } = await api.metrics.getRules(this.organizationId)
        const metricsGroupByType = _.groupBy(data, 'metric.type')

        const incrementRequiredMetrics = _.orderBy(
          metricsGroupByType[this.$constants.metricsType.increment]?.filter(
            metric => metric?.metric?.required === true
          ),
          [item => this.$i18n.t(`impacts.${item.metric?.name}`).toLowerCase()],
          'asc'
        )

        const incrementMetrics = _.orderBy(
          metricsGroupByType[this.$constants.metricsType.increment]?.filter(
            metric => metric?.metric?.required === false
          ),
          [item => this.$i18n.t(`impacts.${item.metric?.name}`).toLowerCase()],
          'asc'
        )

        const reductionRequiredMetrics = _.orderBy(
          metricsGroupByType[this.$constants.metricsType.reduction]?.filter(
            metric => metric?.metric?.required === true
          ),
          [item => this.$i18n.t(`impacts.${item.metric?.name}`).toLowerCase()],
          'asc'
        )

        const reductionMetrics = _.orderBy(
          metricsGroupByType[this.$constants.metricsType.reduction]?.filter(
            metric => metric?.metric?.required === false
          ),
          [item => this.$i18n.t(`impacts.${item.metric?.name}`).toLowerCase()],
          'asc'
        )

        this.incrementMetrics = [
          ...incrementRequiredMetrics,
          ...incrementMetrics,
        ]

        this.reductionMetrics = [
          ...reductionRequiredMetrics,
          ...reductionMetrics,
        ]
      } catch (err) {
        this.handleRequestError(err)
      }
    },
  },
}
</script>
